import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { useParams, useHistory } from 'react-router'
import { GrPrevious, GrNext } from 'react-icons/gr'
import { IoInformationCircle } from 'react-icons/io5'
import { BsStars } from 'react-icons/bs'
import { FaShapes } from 'react-icons/fa6'
import { RxReset } from 'react-icons/rx'
import {
    MdOutlineArrowBack,
    MdFullscreen,
} from 'react-icons/md'

import ContentEngine from '../ContentEngine/ContentEngine'
import { deviceMismatch as deviceMismatchAction } from '../../redux/actions/index'
import MakeContext from './MakeContext'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import Sizes from '../../helpers/Sizes'
import './Make.css'

import PlaydynamexNew from '../PlaydynamexNew/PlaydynamexNew'
import IntegrateNew from '../IntegrateNew/IntegrateNew'
import AssemblyTwo from '../AssemblyTwo/index'
import LogicTwo from '../logicTwo/index'
import SimulateNew from '../SimulateNew/SimulateNew'
import MicroPythonScreen from '../MicroPython/MicroPythonScreen'
import PlayScreen from '../PlayScreen/PlayScreen'

const BACKEND_URL = process.env.REACT_APP_API_BASE

function SubprogramRunner({ assemblyTwo, preview = false }) {
    const { subId } = useParams()
    const history = useHistory()
    const dispatch = useDispatch()
    // Where to send the user when the subprogram finishes / they exit.
    // In admin preview mode we return to the admin detail page instead of
   
    const expansionKit = sessionStorage.getItem('expansionKit')
    const exitTarget = preview
        ? `/makeAdmin/${subId}`
        : expansionKit
        ? `/make/${subId}`
        : '/make'
    const [, forceUpdate] = useState(0)
    const [ready, setReady] = useState(false)

    const [showCompletion, setShowCompletion] = useState(false)
    const [completedTitle, setCompletedTitle] = useState('')
    const timeoutRef = useRef(null)
    // Tracks the in-flight completion sync so navigation waits for it
    const syncRef = useRef(null)

    // --- Resume modal state ---
    const [showResumeModal, setShowResumeModal] = useState(false)
    const [resumeFromIndex, setResumeFromIndex] = useState(0)
    const [resumeLocalStep, setResumeLocalStep] = useState(1)
    // Incremented on "Start Fresh" to force the screen component to remount
    const [freshKey, setFreshKey] = useState(0)

    // --- Save feedback toast ---
    const [showSavedToast, setShowSavedToast] = useState(false)
    const savedToastTimerRef = useRef(null)

    // --- Unsaved progress tracking ---
    // currentLocalStep mirrors what the active screen reports via setFooterProps
    const [currentLocalStep, setCurrentLocalStep] = useState(1)
    const currentLocalStepRef = useRef(1)
    // lastSaved: the { screenIndex, localStep } snapshot at the time of last explicit save
    // null means "never saved in this session" — any real progress counts as unsaved
    const [lastSaved, setLastSaved] = useState(null)
    const hasUnsavedProgressRef = useRef(false)

    // --- Exit-with-unsaved modal ---
    const [showExitModal, setShowExitModal] = useState(false)
    // Thunk stored here while the exit modal is open; called after user confirms
    const pendingExitRef = useRef(null)

    // --- Shell: persistent header + footer state ---
    const [shellHeaderProps, setShellHeaderProps] = useState(null)
    const [shellFooterProps, setShellFooterProps] = useState(null)
    const [shellToolbarProps, setShellToolbarProps] = useState(null)

    // --- Shell: persistent voiceover audio ---
    // A single <audio> element that lives in the shell (never remounts during
    // screen transitions). Screens play step audio *through* this element via
    // context. Because it is unlocked once by a real user gesture, later
    // programmatic playback (e.g. after a cross-screen jump that remounts the
    // target screen and fetches its data async) is no longer blocked by the
    // browser autoplay policy — which is why audio failed on timeline/next nav.
    const audioRef = useRef(null)
    const audioUnlockedRef = useRef(false)

    useEffect(() => {
        // Unlock the shared audio element on the first user interaction so it
        // becomes "user-activated" for the rest of the session.
        const unlock = () => {
            const a = audioRef.current
            if (!a || audioUnlockedRef.current) return
            audioUnlockedRef.current = true
            a.muted = true
            // A zero-length silent WAV — just enough to activate the element.
            a.src =
                'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAgD4AAAB9AAACABAAZGF0YQAAAAA='
            const p = a.play()
            const done = () => {
                a.pause()
                try {
                    a.currentTime = 0
                } catch (e) {}
                a.muted = false
                a.removeAttribute('src')
            }
            if (p && p.then) p.then(done).catch(() => (a.muted = false))
            else done()
        }
        window.addEventListener('pointerdown', unlock)
        window.addEventListener('keydown', unlock)
        return () => {
            window.removeEventListener('pointerdown', unlock)
            window.removeEventListener('keydown', unlock)
        }
    }, [])

    // Stable setters so screens can call them without triggering excess re-renders
    const setHeaderProps = useCallback(
        (props) => setShellHeaderProps(props),
        []
    )
    const setFooterProps = useCallback((props) => {
        setShellFooterProps(props)
        // Extract the current local step from the "Step X of Y" counter string
        // so SubprogramRunner can detect unsaved progress without touching every
        // individual screen component.
        if (props?.stepCounter) {
            const m = props.stepCounter.match(/Step (\d+) of/)
            if (m) {
                const step = parseInt(m[1], 10)
                currentLocalStepRef.current = step
                setCurrentLocalStep(step)
            }
        }
    }, [])
    const setToolbarProps = useCallback(
        (props) => setShellToolbarProps(props),
        []
    )

    // Play/stop step audio through the persistent shell element.
    const playAudio = useCallback((url) => {
        const a = audioRef.current
        if (!a || !url) return
        a.muted = false
        a.src = url
        const p = a.play()
        if (p && p.catch) p.catch((e) => console.log('Playback failed:', e))
    }, [])

    const stopAudio = useCallback(() => {
        const a = audioRef.current
        if (!a) return
        a.pause()
        try {
            a.currentTime = 0
        } catch (e) {}
    }, [])

    // --- Save progress handler ---
    const handleSaveProgress = useCallback(() => {
        // Save at the last EARNED (highlighted) position, not at whatever step
        // the user jumped to via circle navigation.
        const hwm = ContentEngine.globalHwm
        let screenIndexToSave = ContentEngine.screenIndex
        let localStepToSave = currentLocalStepRef.current

        if (hwm > 0) {
            const screens = ContentEngine.getAllScreenSteps()
            const currentScreenInfo = screens.find(
                (s) => s.screenIndex === ContentEngine.screenIndex
            )
            const currentOffset = currentScreenInfo ? currentScreenInfo.offset : 0
            const currentGlobalStep = currentOffset + currentLocalStepRef.current

            if (currentGlobalStep > hwm) {
                // User jumped ahead — clamp save position back to hwm
                for (const screen of screens) {
                    if (hwm >= screen.offset + 1 && hwm <= screen.offset + screen.stepCount) {
                        screenIndexToSave = screen.screenIndex
                        localStepToSave = hwm - screen.offset
                        break
                    }
                }
            }
        }

        const snapshot = {
            screenIndex: screenIndexToSave,
            localStep: localStepToSave,
        }

        // Completed steps are earned sequentially, so the highlighted set is
        // exactly [1..hwm]. This is the checkpoint of highlights to persist.
        const completedSteps = []
        for (let i = 1; i <= hwm; i++) completedSteps.push(i)

        const token = localStorage.getItem('userJWT')
        if (token) {
            fetch(`${BACKEND_URL}/api/progress/sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                credentials: 'include',
                body: JSON.stringify({
                    subprogramId: subId,
                    screenIndex: screenIndexToSave,
                    completedSteps,
                }),
            }).catch((e) => console.error('Failed to save progress', e))
        }

        // Persist the checkpoint: position + highlighted steps. This is the ONLY
        // place progress is written, so "Exit without saving" reverts to whatever
        // was last saved here instead of the latest in-session step.
        localStorage.setItem(`savedStep_${subId}`, JSON.stringify(snapshot))
        localStorage.setItem(
            `completedSteps_${subId}`,
            JSON.stringify(completedSteps)
        )
        setLastSaved(snapshot)

        if (savedToastTimerRef.current) clearTimeout(savedToastTimerRef.current)
        setShowSavedToast(true)
        savedToastTimerRef.current = setTimeout(() => setShowSavedToast(false), 2500)
    }, [subId])

    // --- Exit-with-unsaved handlers ---
    const handleExitSaveAndLeave = useCallback(() => {
        handleSaveProgress()
        setShowExitModal(false)
        if (pendingExitRef.current) {
            // Small delay so the toast is visible before navigation
            setTimeout(() => {
                pendingExitRef.current()
                pendingExitRef.current = null
            }, 600)
        }
    }, [handleSaveProgress])

    const handleExitWithoutSaving = useCallback(() => {
        setShowExitModal(false)
        // Wipe the per-screen step positions every screen type auto-saves to
        // sessionStorage on each step change (playScreen_step_, playdynamex_step_,
        // logic_step_, micropython_step_, …). Without this the screen would
        // restore the exact step the user was on — including a circle-jumped one.
        // Resume still derives the real position from the saved completed-steps
        // (hwm) on re-entry.
        Object.keys(sessionStorage).forEach((k) => {
            if (k.includes('_step_')) sessionStorage.removeItem(k)
        })
        if (pendingExitRef.current) {
            pendingExitRef.current()
            pendingExitRef.current = null
        } else {
            // Fallback: always leave even if the pending-exit action was lost.
            history.push(exitTarget)
        }
    }, [history, exitTarget])

    const handleCancelExit = useCallback(() => {
        setShowExitModal(false)
        pendingExitRef.current = null
    }, [])

    // Memoised context value – the object reference stays stable so context
    // consumers (screen components) don't re-render just from a footer update
    const tokiContextValue = useMemo(
        () => ({
            isMake: true,
            setHeaderProps,
            setFooterProps,
            setToolbarProps,
            playAudio,
            stopAudio,
            saveProgress: handleSaveProgress,
        }),
        [setHeaderProps, setFooterProps, setToolbarProps, playAudio, stopAudio, handleSaveProgress]
    )

    // hasUnsavedProgress: true when the user has advanced beyond the last saved
    // snapshot. Recomputed on every render; ContentEngine.screenIndex is fresh
    // because forceUpdate fires on every ContentEngine notification.
    const hasUnsavedProgress =
        !preview &&
        lastSaved !== null &&
        (ContentEngine.screenIndex !== lastSaved.screenIndex ||
            currentLocalStep !== lastSaved.localStep)
    // Keep a ref so async callbacks (beforeunload, pendingExitRef) read the
    // latest value without needing it in their dependency arrays.
    hasUnsavedProgressRef.current = hasUnsavedProgress

    // Browser close / refresh — show the native "leave site?" dialog.
    useEffect(() => {
        const handler = (e) => {
            if (!hasUnsavedProgressRef.current) return
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, []) // ref keeps handler always current; no dep re-registrations needed

    // --- NEW: Track if the screen is in portrait mode ---
    // const [isPortrait, setIsPortrait] = useState(
    //     window.innerHeight * 1.5 >= window.innerWidth
    // )

    // --- NEW: Check User Agent for mobile and track portrait mode ---
    const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
        )

    const [isMobilePortrait, setIsMobilePortrait] = useState(
        isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
    )

    useEffect(() => {
        // --- NEW: Resize listener to check aspect ratio dynamically ---
        const handleResize = () => {
            setIsMobilePortrait(
                isMobileDevice && window.innerHeight * 1.4 >= window.innerWidth
            )
        }
        window.addEventListener('resize', handleResize)

        const unsub = ContentEngine.subscribe(() => {
            if (
                !preview &&
                typeof ContentEngine.screenIndex === 'number' &&
                !isNaN(ContentEngine.screenIndex)
            ) {
                const token = localStorage.getItem('userJWT')
                if (token) {
                    fetch(`${BACKEND_URL}/api/progress/sync`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`,
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            subprogramId: subId,
                            screenIndex: ContentEngine.screenIndex,
                        }),
                    }).catch((err) =>
                        console.error('Failed to sync screen index', err)
                    )
                } else {
                    
                    try {
                        localStorage.setItem(
                            `screenIndex_${subId}`,
                            String(ContentEngine.screenIndex)
                        )
                    } catch {}
                }
            }
            forceUpdate((x) => x + 1)
        })

        // When the user navigates back past the first screen, check for unsaved
        // progress before exiting the tutorial. If there's nothing unsaved, leave
        // immediately; otherwise show the modal so they can save first.
        ContentEngine.onExitRequest = () => {
            if (hasUnsavedProgressRef.current) {
                pendingExitRef.current = () => history.push(exitTarget)
                setShowExitModal(true)
            } else {
                history.push(exitTarget)
            }
        }

        ContentEngine.onSubprogramComplete = () => {
            const token = localStorage.getItem('userJWT')

            // Store the sync promise so navigating can wait for it to settle.
            // Skip the DB write entirely in preview mode — admins shouldn't
            // mutate learner progress just by previewing a draft.
            if (token && !preview) {
                syncRef.current = fetch(`${BACKEND_URL}/api/progress/sync`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        subprogramId: subId,
                        isCompleted: true,
                    }),
                }).catch((err) =>
                    console.error('Failed to sync completion', err)
                )
            } else {
                syncRef.current = Promise.resolve()
            }

            const currentSubprogram = ContentEngine.getCurrentSubprogram()
            setCompletedTitle(currentSubprogram?.title || 'Project')
            setShowCompletion(true)

            // Wait for DB write to settle before navigating so that
            // GET /api/progress/all in Make sees the new record
            timeoutRef.current = setTimeout(() => {
                ;(syncRef.current || Promise.resolve()).then(() => {
                    history.push(exitTarget, { justCompleted: subId })
                })
            }, 3000)
        }

        // 3. FETCH PROGRESS BEFORE LOADING THE PROGRAM
        const initializeProgram = async () => {
            
            dispatch(deviceMismatchAction({ deviceMismatched: {} }))

            // Route ContentEngine to the staging (unpublished) endpoints when
            // previewing, and back to live otherwise. Set before loadProgram.
            ContentEngine.staging = preview

            let savedIndex = 0
            let savedCompletedSteps = []
            const token = localStorage.getItem('userJWT')

            
            if (!token && !preview) {
                try {
                    const localIdx = localStorage.getItem(`screenIndex_${subId}`)
                    if (localIdx !== null && !isNaN(localIdx)) {
                        savedIndex = parseInt(localIdx, 10)
                    }
                } catch {}
            }

            // Fetch progress from DB (skip in preview — always start at screen 0)
            if (token && !preview) {
                try {
                    const res = await fetch(
                        `${BACKEND_URL}/api/progress/${subId}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                            credentials: 'include',
                        }
                    )
                    if (res.ok) {
                        const data = await res.json()
                        if (
                            data.progress &&
                            data.progress.screenIndex !== undefined
                        ) {
                            savedIndex = data.progress.screenIndex
                        }
                        // Stash completedSteps so we can restore globalHwm after
                        // loadProgram resets it to 0 (see below).
                        if (
                            data.progress &&
                            Array.isArray(data.progress.completedSteps) &&
                            data.progress.completedSteps.length > 0
                        ) {
                            savedCompletedSteps = data.progress.completedSteps
                        }
                    }
                } catch (e) {
                    console.error('Error fetching progress from DB', e)
                }
            }

            // The exact resume position (screen + step) is derived from the
            // completed-steps list (hwm) AFTER the program loads below, so we can
            // map the global step onto the right screen. savedStep_ is no longer
            // read for positioning because it can point at a circle-jumped step.

            // Load Engine — resolve which board/program this subprogram
            // belongs to (Toki, Poki, …) from the device the admin/learner
            // selected. Hardcoding 'toki' meant Poki subprograms were never
            // found, so the Start Preview button silently bounced back.
            
            const kitDevice = sessionStorage.getItem('allowedDevice')
            const expansionKit = sessionStorage.getItem('expansionKit')

            
            if (kitDevice && expansionKit) {
                const { EXPANSION_KITS } = await import('../../utils/expansionKits')
                const kit = EXPANSION_KITS[expansionKit]
                if (kit && !kit.devices.includes(kitDevice)) {
                    sessionStorage.removeItem('allowedDevice')
                }
            }

            const programId = (
                sessionStorage.getItem('allowedDevice') || 'toki'
            ).toLowerCase()

            if (!sessionStorage.getItem('allowedDevice')) {
                sessionStorage.setItem(
                    'allowedDevice',
                    programId.charAt(0).toUpperCase() + programId.slice(1)
                )
            }
            await ContentEngine.loadProgram(programId)

            // loadProgram() resets globalHwm/globalLastNext to 0. Restore the
            // completed-steps list: backend value wins, else fall back to
            // localStorage so logged-out expansion-kit users keep their progress.
            if (savedCompletedSteps.length === 0 && !preview) {
                try {
                    const localRaw = localStorage.getItem(`completedSteps_${subId}`)
                    if (localRaw) {
                        const parsed = JSON.parse(localRaw)
                        if (Array.isArray(parsed) && parsed.length > 0) {
                            savedCompletedSteps = parsed
                        }
                    }
                } catch {}
            }

            const subIdx = ContentEngine.program?.subprograms?.findIndex(
                (s) => s.id === subId
            )

            if (subIdx !== undefined && subIdx >= 0) {
                // Set subIndex FIRST so getAllScreenSteps()/offsets resolve
                // against the correct subprogram.
                ContentEngine.subIndex = subIdx

                const currentSub = ContentEngine.program.subprograms[subIdx]
                const maxIndex = currentSub?.screens
                    ? currentSub.screens.length - 1
                    : 0

                // hwm = highest earned (highlighted) global step — the single
                // source of truth for where the learner resumes.
                let hwm = 0
                if (savedCompletedSteps.length > 0 && !preview) {
                    hwm = Math.max(...savedCompletedSteps)
                    ContentEngine.globalHwm = hwm
                    ContentEngine.globalLastNext = hwm
                }

                // Map hwm (a global step number) → (screenIndex, localStep) so we
                // resume exactly at the last highlighted step, never a jumped one.
                let resumeIdx = savedIndex
                let resumeStep = 1
                if (hwm > 0) {
                    const screens = ContentEngine.getAllScreenSteps()
                    for (const screen of screens) {
                        if (
                            hwm >= screen.offset + 1 &&
                            hwm <= screen.offset + screen.stepCount
                        ) {
                            resumeIdx = screen.screenIndex
                            resumeStep = hwm - screen.offset
                            break
                        }
                    }
                }

                // Validate index bounds
                if (isNaN(resumeIdx) || resumeIdx < 0 || resumeIdx > maxIndex) {
                    resumeIdx = 0
                    resumeStep = 1
                }

                // Expose the resume target to the modal handlers + anchor the
                // "last saved" snapshot so hasUnsavedProgress starts false.
                setResumeFromIndex(resumeIdx)
                setResumeLocalStep(resumeStep)
                setLastSaved({ screenIndex: resumeIdx, localStep: resumeStep })

                const hasSavedProgress = !preview && hwm > 0

                if (hasSavedProgress) {
                    // Any learner with saved progress (logged in or not) gets the
                    // choice: resume from the last highlighted step or start fresh.
                    // Hold at the first screen until they pick — handleContinue
                    // applies resumeStep, handleStartFresh resets to step 1.
                    setShowResumeModal(true)
                    ContentEngine.screenIndex = 0
                } else {
                    // No saved progress — start at the beginning.
                    ContentEngine.screenIndex = resumeIdx
                }

                ContentEngine.loadCurrentScreenData().then(() => {
                    ContentEngine.notify()
                    setReady(true)
                })
            } else {
                console.error('Subprogram not found:', subId)
                history.push(preview ? exitTarget : '/kitSelection')
            }
        }

        initializeProgram()

        return () => {
            unsub()
            ContentEngine.onSubprogramComplete = null
            ContentEngine.onExitRequest = null
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            window.removeEventListener('resize', handleResize)
        }
    }, [subId, history, preview, exitTarget])

    const handleOverlayClick = () => {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current)
            // Await the sync before navigating (handles immediate overlay clicks)
        ;(syncRef.current || Promise.resolve()).then(() => {
            history.push(exitTarget, { justCompleted: subId })
        })
    }

    // --- Resume modal handlers ---
    const handleContinue = useCallback(() => {
        // Compute the resume position from globalHwm (last earned/highlighted step)
        // rather than the raw saved state, which may point to a circle-jumped step.
        const hwm = ContentEngine.globalHwm
        let screenIdx = resumeFromIndex
        let localStep = resumeLocalStep

        if (hwm > 0) {
            const screens = ContentEngine.getAllScreenSteps()
            for (const screen of screens) {
                if (hwm >= screen.offset + 1 && hwm <= screen.offset + screen.stepCount) {
                    screenIdx = screen.screenIndex
                    localStep = hwm - screen.offset
                    break
                }
            }
        }

        ContentEngine._pendingStep = localStep
        // Restore the earning chain BEFORE any screen load so it's ready
        // the moment the user presses Next.
        ContentEngine.globalLastNext = ContentEngine.globalHwm

        const currentIdx = ContentEngine.screenIndex
        ContentEngine.screenIndex = screenIdx

        const commit = () => {
            // freshKey forces the content div to get a new React key, which
            // unmounts and remounts the screen component. This is necessary
            // even for same-screen resumes because useState initializers only
            // run on mount — updating the initialStep prop alone does nothing
            // if the component is already mounted.
            setFreshKey((k) => k + 1)
            setShowResumeModal(false)
        }

        if (screenIdx !== currentIdx) {
            // Different screen: load its data first, then remount.
            ContentEngine.loadCurrentScreenData().then(() => {
                ContentEngine.notify()
                commit()
            })
        } else {
            // Same screen: data already loaded, just remount at the saved step.
            commit()
        }
    }, [resumeFromIndex, resumeLocalStep])

    const handleStartFresh = useCallback(async () => {
        const token = localStorage.getItem('userJWT')
        if (token) {
            fetch(`${BACKEND_URL}/api/progress/sync`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                credentials: 'include',
                body: JSON.stringify({
                    subprogramId: subId,
                    screenIndex: 0,
                    completedSteps: [],
                }),
            }).catch((e) => console.error('Failed to reset progress', e))
        }
        // Clear stored step-within-screen positions for this subprogram
        Object.keys(localStorage).forEach((k) => {
            if (k.startsWith(`progress_step_${subId}_`)) localStorage.removeItem(k)
        })
        // Clear the saved local step and completed steps so fresh start begins at step 1
        localStorage.removeItem(`savedStep_${subId}`)
        localStorage.removeItem(`completedSteps_${subId}`)
        // Clear every screen type's per-step position (playScreen_step_,
        // playdynamex_step_, logic_step_, micropython_step_, …) so none of the
        // screens restore a stale step after the fresh start.
        Object.keys(sessionStorage).forEach((k) => {
            if (k.includes('_step_')) sessionStorage.removeItem(k)
        })
        ContentEngine.screenIndex = 0
        // Reset the sequential-earning chain so step 1 is immediately earnable
        ContentEngine.globalHwm = 0
        ContentEngine.globalLastNext = 0
        ContentEngine.globalHwmJustEarned = false
        // Force the remounted screen to land on step 1. Without this, PlayScreen's
        // useState initializer falls back to sessionStorage which may still hold the
        // old step for the current browser session even after the clear above.
        ContentEngine._pendingStep = 1
        // Tell ProgramTimelineNav to clear its completedSteps on the next
        // notify tick. Using a flag + the existing notify/subscribe bus is
        // more reliable than a direct callback because the bus already drives
        // all timeline re-renders and fires before React batches state updates.
        ContentEngine._pendingProgressReset = true
        ContentEngine.loadCurrentScreenData().then(() => ContentEngine.notify())
        // Increment freshKey so the screen div gets a new key, forcing logicTwo
        // to unmount and remount cleanly at step 1
        setFreshKey((k) => k + 1)
        setShowResumeModal(false)
        // Reset unsaved-progress tracking to a clean baseline
        currentLocalStepRef.current = 1
        setCurrentLocalStep(1)
        setLastSaved({ screenIndex: 0, localStep: 1 })
    }, [subId])

    const screen = ContentEngine.getCurrentScreen()
    const data = ContentEngine.currentScreenData
    const screenKey = `${ContentEngine.subIndex}-${ContentEngine.screenIndex}`
    const slideClass = 'toki-dissolve-in'
    const projectId =
        screen?.projectId || ContentEngine.getCurrentSubprogram()?.id || null
    const screenId = screen?.id || null

    if (projectId) {
        sessionStorage.setItem('apiProjectId', projectId)
    }
    // Drive the assembly component catalog off the active board instead of
    // always forcing Toki, so Poki (and future boards) surface their own parts.
    const allowedDevice = sessionStorage.getItem('allowedDevice') || 'Toki'
    const connectedDevice =
        allowedDevice.charAt(0).toUpperCase() +
        allowedDevice.slice(1).toLowerCase()
    sessionStorage.setItem('connectedDevice', connectedDevice)

    // Preserve the existing deviceVersion instead of resetting to '1'
    if (!sessionStorage.getItem('deviceVersion')) {
        sessionStorage.setItem('deviceVersion', '1')
    }

    let assemblyScreenId = null
    if (screen?.type === 'hex') {
        const sub = ContentEngine.getCurrentSubprogram()
        if (sub?.screens) {
            for (let i = ContentEngine.screenIndex - 1; i >= 0; i--) {
                if (sub.screens[i].type === 'assembly') {
                    assemblyScreenId = sub.screens[i].id
                    break
                }
            }
        }
        if (assemblyScreenId) {
            sessionStorage.setItem('assemblyScreenId', assemblyScreenId)
        }
    }

    if (!ready || !screen || !data) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    fontSize: '1.2rem',
                    color: '#888',
                }}
            >
                {/* Loading... */}
            </div>
        )
    }

    // Read the pending resume step ONLY now that we're actually mounting the
    // screen (ready === true). Reading it earlier — during the loading renders
    // that fire while the program loads — would schedule it cleared via the
    // setTimeout below before the screen ever receives it, so resume would fall
    // back to the screen's own sessionStorage (a circle-jumped step).
    const pendingStep = ContentEngine._pendingStep
    if (pendingStep != null) {
        setTimeout(() => {
            ContentEngine._pendingStep = null
        }, 0)
    }

    return (
        <div className="make-page toki-shell-active">
            {/* ── Persistent voiceover audio (never unmounts across screens) ── */}
            <audio ref={audioRef} style={{ display: 'none' }} />

            {/* ── Admin preview banner / exit button ── */}
            {preview && !isMobilePortrait && (
                <button
                    onClick={() => history.push(exitTarget)}
                    title="Return to the admin editor"
                    style={{
                        position: 'fixed',
                        top: '102px',
                        right: '52px',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 14px',
                        borderRadius: '999px',
                        border: 'none',
                        background: '#DC3545',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
                    }}
                >
                    <MdOutlineArrowBack size={18} /> Exit Preview
                </button>
            )}

            {/* ── Persistent header (never unmounts during screen transitions) ── */}
            {shellHeaderProps && !isMobilePortrait && (
                <div className="toki-shell-header">
                    <MakeHeader
                        showTimeline
                        {...shellHeaderProps}
                        isGoBack={true}
                        goBack={() => ContentEngine.prevToStart()}
                        showSave={false}
                        isSaveToBoard={true}
                        fileSaveClick={handleSaveProgress}
                    />
                </div>
            )}

            {/* ── Sliding content area ── */}
            <div className="make-content">
                <div
                    key={`${screenKey}-${freshKey}`}
                    className={slideClass}
                    style={{ width: '100%', height: '100%' }}
                >
                    <MakeContext.Provider value={tokiContextValue}>
                        {renderScreen(
                            screen,
                            data,
                            screenKey,
                            assemblyTwo,
                            projectId,
                            screenId,
                            assemblyScreenId,
                            pendingStep
                        )}
                    </MakeContext.Provider>
                </div>
            </div>

            {/* ── Persistent footer (never unmounts during screen transitions) ── */}
            {shellFooterProps && !isMobilePortrait && (
                <footer className="toki-shell-footer">
                    <div className="step-counter">
                        {shellFooterProps.stepCounter}
                    </div>
                    <div className="step-title">
                        {shellFooterProps.stepTitle}
                    </div>
                    <div className="nav-buttons">
                        <button
                            className="nav-btn"
                            onClick={shellFooterProps.onPrev}
                            disabled={!!shellFooterProps.prevDisabled}
                        >
                            <GrPrevious />
                            <span className="footer-btn-text">Previous</span>
                        </button>
                        <div className="next-btn-wrapper">
                            {shellFooterProps.showNextTooltip && (
                                <div className="next-btn-tooltip">
                                    <IoInformationCircle />{' '}
                                    {shellFooterProps.nextTooltipText ||
                                        'Complete the action to proceed'}
                                </div>
                            )}
                            <button
                                className={`nav-btn ${shellFooterProps.nextClassName || ''}`}
                                onClick={shellFooterProps.onNext}
                                disabled={!!shellFooterProps.nextDisabled}
                            >
                                <span className="footer-btn-text">
                                    {shellFooterProps.nextLabel || 'Next'}
                                </span>
                                <GrNext />
                            </button>
                        </div>
                    </div>

                </footer>
            )}

            {/* ── Persistent toolbar (never unmounts during screen transitions) ── */}
            {shellToolbarProps && !isMobilePortrait && (
                <div className="toki-shell-toolbar">
                    {Array.isArray(shellToolbarProps.customButtons) &&
                        shellToolbarProps.customButtons.map((b) => (
                            <button
                                key={b.key}
                                className={`toolbar-button ${b.active ? 'active' : ''} ${b.highlight ? 'mp-action-highlight' : ''}`}
                                style={b.isStop ? { background: '#ef4444', color: '#fff', borderRadius: '50%' } : undefined}
                                title={b.title}
                                onClick={b.onClick}
                                disabled={b.disabled}
                            >
                                {b.icon}
                            </button>
                        ))}
                    {/* Materials toggle hidden in expansion kit screens
                    {shellToolbarProps.onMaterialsToggle && (
                        <button
                            className={`toolbar-button ${shellToolbarProps.showMaterials ? 'active' : ''}`}
                            title="Materials"
                            onClick={shellToolbarProps.onMaterialsToggle}
                        >
                            <FaShapes />
                        </button>
                    )} */}
                    <button
                        className={`toolbar-button ${shellToolbarProps.aiVoiceover ? 'active' : ''}`}
                        title="AI Voiceover"
                        onClick={shellToolbarProps.onAiToggle}
                    >
                        <BsStars />
                    </button>
                    {shellToolbarProps.stepPopupEnabled && (
                        <button
                            className="toolbar-button"
                            title="Step Instructions"
                            onClick={shellToolbarProps.onShowStepPopup}
                            style={{ position: 'relative' }}
                        >
                            <IoInformationCircle />
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '4px',
                                    right: '4px',
                                    width: '7px',
                                    height: '7px',
                                    background: '#f59e0b',
                                    borderRadius: '50%',
                                }}
                            />
                        </button>
                    )}
                    <button
                        className="toolbar-button"
                        title="Fullscreen"
                        onClick={shellToolbarProps.onFullscreen}
                    >
                        <MdFullscreen />
                    </button>
                    <button
                        className="toolbar-button"
                        title="Reset"
                        onClick={shellToolbarProps.onReset}
                    >
                        <RxReset />
                    </button>
                </div>
            )}

            {/* --- NEW: The Portrait/Mobile Restriction Overlay --- */}
            {isMobilePortrait && (
                <div className="portrait-overlay">
                    <div className="completion-content">
                        <div className="portrait-icon">
                            <svg viewBox="0 0 24 24" width="80" height="80">
                                <rect
                                    x="2"
                                    y="4"
                                    width="20"
                                    height="12"
                                    rx="2"
                                    fill="none"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M6 20h12M12 16v4"
                                    stroke="#f8b26a"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <h1 className="portrait-title">Switch Device</h1>
                        <p className="portrait-subtitle">
                            Please access this content on a{' '}
                            <strong>PC, Laptop, or a Landscape Tablet</strong>.
                            Portrait ratios and mobile devices are not
                            supported.
                        </p>
                    </div>
                </div>
            )}

            {/* --- Resume Modal --- */}
            {showResumeModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'hsla(0,0%,100%,.7)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'Halcyon_SemiBold',
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            background: 'linear-gradient(to right, #f0eded, white)',
                            borderRadius: '3rem',
                            border: '1px solid white',
                            boxShadow: '0.5rem 0.5rem 2rem #d9d5d4',
                            animation: 'pdx-popup-in 0.22s ease',
                            padding: '1.5rem 2rem',
                            maxWidth: '400px',
                            width: '90%',
                            textAlign: 'center',
                        }}
                    >
                        <h2
                            style={{
                                margin: '0 0 10px',
                                fontSize: '1.3rem',
                                color: '#1f1f4e',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                            }}
                        >
                            Welcome back!
                        </h2>
                        <p style={{ margin: '0 0 28px', color: '#444', fontSize: '1rem', lineHeight: 1.65 }}>
                            You have saved progress on this tutorial. Would you like to continue where you left off?
                        </p>
                        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center' }}>
                            <button
                                onClick={handleStartFresh}
                                style={{
                                    padding: '10px 24px',
                                    borderRadius: '999px',
                                    border: '2px solid #e0e0e0',
                                    background: '#fff',
                                    color: '#555',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    fontFamily: 'Halcyon_SemiBold, sans-serif',
                                    cursor: 'pointer',
                                }}
                            >
                                Start Fresh
                            </button>
                            <button
                                onClick={handleContinue}
                                style={{
                                    padding: '10px 28px',
                                    borderRadius: '999px',
                                    border: 'none',
                                    background: '#ff8c42',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.9rem',
                                    fontFamily: 'Halcyon_SemiBold, sans-serif',
                                    cursor: 'pointer',
                                }}
                            >
                                Continue
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Progress Saved Toast --- */}
            {showSavedToast && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: '80px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(34,139,87,0.93)',
                        color: '#fff',
                        padding: '11px 28px',
                        borderRadius: '999px',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        zIndex: 10000,
                        pointerEvents: 'none',
                        boxShadow: '0 4px 18px rgba(0,0,0,0.22)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        letterSpacing: '0.01em',
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="12" fill="rgba(255,255,255,0.25)" />
                        <path d="M7 12l3 3 7-7" stroke="#fff" strokeWidth="2.5"
                            strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Progress saved!
                </div>
            )}

            {/* --- Unsaved Progress / Exit Warning Modal --- */}
            {showExitModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'hsla(0,0%,100%,.7)',
                        zIndex: 10001,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'Halcyon_SemiBold',
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            background: 'linear-gradient(to right, #f0eded, white)',
                            borderRadius: '3rem',
                            border: '1px solid white',
                            boxShadow: '0.5rem 0.5rem 2rem #d9d5d4',
                            animation: 'pdx-popup-in 0.22s ease',
                            padding: '1.5rem 2rem',
                            maxWidth: '400px',
                            width: '90%',
                            textAlign: 'center',
                        }}
                    >
                        {/* Close button — top-right diagonal corner (matches instructions popup) */}
                        <button
                            onClick={handleCancelExit}
                            style={{
                                position: 'absolute',
                                top: '-5px',
                                right: '-5px',
                                width: '2.2rem',
                                height: '2.2rem',
                                background: '#e57373',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 12px rgba(229,115,115,0.4)',
                                padding: '0',
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 1L11 11M11 1L1 11" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
                            </svg>
                        </button>

                        <h2
                            style={{
                                margin: '0 0 10px',
                                fontSize: '1.3rem',
                                color: '#1f1f4e',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                            }}
                        >
                            Unsaved Progress
                        </h2>
                        <p style={{ margin: '0 0 28px', color: '#444', fontSize: '1rem', lineHeight: 1.65 }}>
                            You have progress that hasn't been saved yet. Would you like to save before leaving?
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <button
                                onClick={handleExitSaveAndLeave}
                                style={{
                                    padding: '12px 0',
                                    borderRadius: '999px',
                                    border: 'none',
                                    background: '#ff8c42',
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    fontFamily: 'Halcyon_SemiBold, sans-serif',
                                    cursor: 'pointer',
                                    width: '100%',
                                }}
                            >
                                Save &amp; Exit
                            </button>
                            <button
                                onClick={handleExitWithoutSaving}
                                style={{
                                    padding: '12px 0',
                                    borderRadius: '999px',
                                    border: '2px solid #e0e0e0',
                                    background: '#fff',
                                    color: '#555',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    fontFamily: 'Halcyon_SemiBold, sans-serif',
                                    cursor: 'pointer',
                                    width: '100%',
                                }}
                            >
                                Exit without saving
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- The Completion Overlay --- */}
            {showCompletion && !isMobilePortrait && (
                <div
                    className="completion-overlay"
                    onClick={handleOverlayClick}
                >
                    <div className="completion-content">
                        <div className="completion-icon">
                            <svg viewBox="0 0 24 24" width="80" height="80">
                                <circle cx="12" cy="12" r="12" fill="#abbd81" />
                                <path
                                    d="M7 12l3 3 7-7"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                        <h1 className="completion-title">Awesome Job!</h1>
                        <h2 className="completion-subtitle">
                            You completed <strong>{completedTitle}</strong>
                        </h2>
                        <p className="completion-hint">
                            Click anywhere to continue
                        </p>
                    </div>
                </div>
            )}
        </div>
    )
}

function renderScreen(
    screen,
    data,
    screenKey,
    assemblyTwo,
    projectId,
    screenId,
    assemblyScreenId,
    initialStep
) {
    switch (screen.type) {
        case 'playdynamex':
            return (
                <PlaydynamexNew
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        case 'integrate':
            return (
                <IntegrateNew
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        case 'assembly':
            const height = assemblyTwo ? assemblyTwo.height : '100%'
            return (
                <AssemblyTwo
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    height={height}
                    width={Sizes.width}
                    initialStep={initialStep}
                />
            )
        case 'hex':
            return (
                <LogicTwo
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    assemblyScreenId={assemblyScreenId}
                    initialStep={initialStep}
                />
            )
        case 'simulate':
            return (
                <SimulateNew
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        case 'play':
            return (
                <PlayScreen
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        case 'micropython':
        case 'c':
            return (
                <MicroPythonScreen
                    key={screenKey}
                    data={data}
                    projectId={projectId}
                    screenId={screenId}
                    initialStep={initialStep}
                />
            )
        default:
            return <div>Unknown screen type: {screen.type}</div>
    }
}

const mapStateToProps = (state) => ({
    assemblyTwo: state.assemblyTwo,
})

export default connect(mapStateToProps)(SubprogramRunner)
